import { v4 as uuidv4 } from "uuid";
import type { Category } from "@/components/estimation-details/Rooms/Lines";
import type { EstimateDetail, Tuple } from "@/interfaces/estimate-detail";
import type { LineItemData } from "@/interfaces/payloads/line-item";
import type { GetEstimateDetailData } from "@/hooks/queries/estimates/useGetEstimateDetailQuery";
import type { IQuantityFormula } from "@/components/estimation-details/Rooms/Lines/util/engine-shared-utils";
import { getDetailFromPossiblyMergedPage } from "./lookup";

export interface DeleteResult {
  modifiedState: EstimateDetail[] | null;
  apiData: {
    pageIdForApi: string;
    tupleData: LineItemData;
    tupleId: string;
  } | null;
}

export interface CreateLineItemInput {
  pageId: string;
  item: string;
  lineItemId: string;
  quantity?: number;
  measurement?: string;
  formula?: IQuantityFormula | null;
  orderIndex?: number;
}

/**
 * Applies update/insert of tuples to estimationDetail via immutable updates — only the rooms
 * that actually change get a new reference; everything else is reused. Use this anywhere we
 * need the new array (Redux state, React Query cache, etc.) without paying for a deep clone of
 * the entire estimate.
 */
export function applyOptimisticUpdateToEstimationDetail(
  estimationDetail: EstimateDetail[],
  params: { pageId: string; lineItemData: LineItemData[]; tupleId?: string },
): EstimateDetail[] {
  const { pageId, lineItemData, tupleId } = params;

  // Group tuple updates by the room index they resolve to (handling merged pages).
  const updatesByRoom = new Map<number, LineItemData[]>();
  for (const tupleData of lineItemData) {
    const { currentPageIndex } = getDetailFromPossiblyMergedPage(estimationDetail, pageId, tupleId || tupleData.tupleId);
    if (currentPageIndex === -1) continue;
    const list = updatesByRoom.get(currentPageIndex) ?? [];
    list.push(tupleData);
    updatesByRoom.set(currentPageIndex, list);
  }

  if (updatesByRoom.size === 0) return estimationDetail;

  return estimationDetail.map((room, idx) => {
    const updates = updatesByRoom.get(idx);
    if (!updates) return room;
    const newTuples = [...room.dataForUI.page.tuplesOutput];
    for (const tupleData of updates) {
      const tupleIdx = newTuples.findIndex((t) => t.tupleId === tupleData.tupleId);
      if (tupleIdx === -1) {
        newTuples.push(tupleData as unknown as Tuple);
      } else {
        newTuples[tupleIdx] = tupleData as unknown as Tuple;
      }
    }
    return {
      ...room,
      dataForUI: {
        ...room.dataForUI,
        page: {
          ...room.dataForUI.page,
          tuplesOutput: newTuples,
        },
      },
    };
  });
}

/**
 *
 * Applies an update (or insert) of tuples to the estimation detail state.
 * Used for optimistic updates when updating/creating line items via the update endpoint.
 *
 * @param currentData - The current estimation detail state
 * @param params - The parameters for the optimistic update
 * @returns The updated estimation detail state
 */
export function applyOptimisticUpdate(
  currentData: GetEstimateDetailData | undefined,
  params: { pageId: string; lineItemData: LineItemData[]; tupleId?: string },
): GetEstimateDetailData | undefined {
  if (!currentData?.estimationDetail) return currentData;
  const next = applyOptimisticUpdateToEstimationDetail(currentData.estimationDetail, params);
  if (next === currentData.estimationDetail) return currentData;
  return { ...currentData, estimationDetail: next };
}

/**
 *
 * Builds the LineItemData structure for a brand-new line item.
 *
 * @param params - The parameters for the new line item
 * @param existingTuplesCount - The number of existing tuples on the page
 * @returns The LineItemData structure for the new line item
 */
export function buildNewLineItemData(params: CreateLineItemInput, existingTuplesCount: number): LineItemData {
  const { item, lineItemId, quantity, measurement, formula, orderIndex } = params;

  return {
    tupleId: `user_${uuidv4()}`,
    tupleRegion: { height: 0, width: 0, left: 0, top: 0 },
    lineItems: [
      {
        uniqueId: uuidv4(),
        leftMapping: [],
        rightMapping: [],
        mappingExtraData: [],
        qnm: [{ quantity, measurement }],
        lineItem: item,
        isComplement: false,
        lineItemId,
        deleted: false,
        addedByTPARule: false,
        confidence: 1,
        confirmed: true,
        formula: formula ?? undefined,
        isMutuallyExcluded: false,
        isExcludedBy: [],
        orderIndex,
      },
    ],
    isLikely: false,
    isComplementOf: "",
    originalTupleIndex: orderIndex ?? existingTuplesCount,
    deleted: false,
  };
}

/**
 *
 * Applies an optimistic create (appends a new tuple) to estimation detail state.
 *
 * @param currentData - The current estimation detail state
 * @param pageId - The page ID
 * @param newTupleData - The new tuple data
 * @returns The updated estimation detail state
 */
export function applyOptimisticCreate(
  currentData: GetEstimateDetailData | undefined,
  pageId: string,
  newTupleData: LineItemData,
): GetEstimateDetailData | undefined {
  if (!currentData?.estimationDetail) return currentData;

  const currentPageIndex = currentData.estimationDetail.findIndex(
    (detail: EstimateDetail) => detail.dataForUI.metadata.pageId === pageId,
  );
  if (currentPageIndex === -1) return currentData;

  const room = currentData.estimationDetail[currentPageIndex];
  const newRoom: EstimateDetail = {
    ...room,
    dataForUI: {
      ...room.dataForUI,
      page: {
        ...room.dataForUI.page,
        tuplesOutput: [...room.dataForUI.page.tuplesOutput, newTupleData as unknown as Tuple],
      },
    },
  };

  return {
    ...currentData,
    estimationDetail: currentData.estimationDetail.map((r, i) => (i === currentPageIndex ? newRoom : r)),
  };
}

/**
 *
 * Applies deletion to a state copy and returns both the modified state
 * and the API payload needed to persist the deletion.
 *
 * @param currentState - The current estimation detail state
 * @param params - The parameters for the deletion
 * @returns The modified state and the API payload needed to persist the deletion
 */
export function applyDeleteToState(
  currentState: EstimateDetail[],
  params: { pageId: string; tupleId: string; uniqueId: string; category?: Category },
): DeleteResult {
  const { pageId, tupleId, uniqueId, category } = params;

  const { currentPageIndex, tuples, tupleIndex, resolvedPageId } = getDetailFromPossiblyMergedPage(currentState, pageId, tupleId);

  if (!currentState[currentPageIndex] || !tuples[tupleIndex]) {
    console.error("[applyDeleteToState] Delete failed: Page or tuple not found");
    return { modifiedState: null, apiData: null };
  }

  const targetTuple = tuples[tupleIndex];
  // Match on the canonical `uniqueId`, not `lineItemId`: `lineItemId` values are not guaranteed
  // unique within a tuple, so matching on them risks archiving the wrong line item.
  const lineItemIndex = targetTuple.lineItems.findIndex((li) => li.uniqueId === uniqueId);

  if (lineItemIndex === -1) {
    console.error("[applyDeleteToState] Delete failed: Line item not found");
    return { modifiedState: null, apiData: null };
  }

  const newLineItems = targetTuple.lineItems.map((li, i) => {
    if (category || i === lineItemIndex) return { ...li, archived: true };
    return li;
  });
  const newTuple: Tuple = {
    ...targetTuple,
    ...(category ? { archived: true } : {}),
    lineItems: newLineItems,
  };
  const newTuples = tuples.map((t, i) => (i === tupleIndex ? newTuple : t));

  const room = currentState[currentPageIndex];
  const newRoom: EstimateDetail = {
    ...room,
    dataForUI: {
      ...room.dataForUI,
      page: {
        ...room.dataForUI.page,
        tuplesOutput: newTuples,
      },
    },
  };
  const modifiedState = currentState.map((r, i) => (i === currentPageIndex ? newRoom : r));

  return {
    modifiedState,
    apiData: {
      pageIdForApi: resolvedPageId,
      tupleData: newTuple as unknown as LineItemData,
      tupleId,
    },
  };
}
